import React, { useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginWithToken } from '../redux/actions/validateAction';
import useCallOnce from '../hooks/useCallOnce';
import { showSuccess, showError } from '../Components/reuse/Toast';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';

function TokenLogin() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const { tokenLoginLoading } = useSelector((state) => state.auth);
  
  const [status, setStatus] = useState('validating'); // validating, success, error
  const [message, setMessage] = useState('');

  // Use useCallOnce to ensure API is only called once per token
  useCallOnce(`tokenLogin/${searchParams.get('token')}`, () => {
    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('No token provided in URL. Please use a valid login link.');
      // showError('No token provided in URL. Please use a valid login link.');
      return;
    }

    // Automatically attempt to login with the token
    handleTokenLogin(token);
  });

  const handleTokenLogin = async (token) => {
    try {
      setStatus('validating');
      setMessage('');
      
      const result = await dispatch(loginWithToken(token));
      
      if (result.success) {
        setStatus('success');
        setMessage('Login successful! Redirecting to dashboard...');
        showSuccess('Login successful! Redirecting to dashboard...');
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 2000);
      } else {
        setStatus('error');
        const errorMessage = result.error || 'Login failed. The token may be invalid or expired.';
        setMessage(errorMessage);
        showError(errorMessage);
      }
    } catch (error) {
      setStatus('error');
      const errorMessage = 'An unexpected error occurred during login. Please try again.';
      setMessage(errorMessage);
      showError(errorMessage);
      console.error('Token login error:', error);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login', { replace: true });
  };

  // If we're on the root path and there's no token, redirect to login
  if (location.pathname === '/' && !searchParams.get('token')) {
    navigate('/login', { replace: true });
    return null;
  }

  if (status === 'validating' || tokenLoginLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f9fafb',
          p: 2,
        }}
      >
        <Card elevation={3} sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress size={60} sx={{ mb: 3, color: '#111827' }} />
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
              Validating Login Token
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
              Please wait while we validate your login token...
            </Typography>
            <Typography variant="caption" sx={{ color: '#9ca3af' }}>
              This may take a few seconds
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f9fafb',
          p: 2,
        }}
      >
        <Card elevation={3} sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#f0fdf4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                border: '2px solid #bbf7d0',
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 40, color: '#16a34a' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
              Login Successful!
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
              {message}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <CircularProgress size={16} sx={{ color: '#16a34a' }} />
              <Typography variant="caption" sx={{ color: '#16a34a' }}>
                Redirecting...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (status === 'error') {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f9fafb',
          p: 2,
        }}
      >
        <Card elevation={3} sx={{ maxWidth: 400, width: '100%' }}>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: '#fef2f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                border: '2px solid #fecaca',
              }}
            >
              <ErrorIcon sx={{ fontSize: 40, color: '#dc2626' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#111827' }}>
              Login Failed
            </Typography>
            <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
              {message}
            </Alert>
            
            <Button
              variant="outlined"
              onClick={handleGoToLogin}
              fullWidth
              sx={{
                borderColor: '#6b7280',
                color: '#6b7280',
                '&:hover': {
                  borderColor: '#374151',
                  bgcolor: '#f9fafb',
                },
              }}
            >
              Go to Login Page
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return null;
}

export default TokenLogin;
